import type { FollowingStore } from "@/types/layout";
import { apiService } from "./apiService";

// ... (keep all existing interfaces)

interface FollowingItem {
  id: string;
  type: string;
  name: string;
  storeName: string;
  profileImage: string;
  followDate: string;
  followerCount: number;
  isLive: boolean;
  itemsCount: number;
}

interface PaginationData {
  total: number;
  pages: number;
  current: number;
  hasMore: boolean;
}

interface FollowingStoresResponse {
  success: boolean;
  message: string;
  data: {
    following: FollowingItem[];
  };
  pagination: PaginationData;
}

interface FollowData {
  buyerId: string;
  firstName: string;
  lastName: string;
  email: string;
  followDate: string;
  totalOrders: number;
  mainAddress: Record<string, unknown>;
}

interface FollowStoreResponse {
  success: boolean;
  message: string;
  data: {
    followData: FollowData;
    sellerFollowCount: number;
    buyerFollowingCount: number;
  };
}

interface UnfollowStoreResponse {
  success: boolean;
  message: string;
  data: {
    sellerFollowCount: number;
    buyerFollowingCount: number;
  };
}

interface GetFollowingStoresParams {
  page?: number;
  limit?: number;
  query?: string;
}

interface BuyerBasicInfo {
  firstName: string;
  lastName: string;
  username: string;
  profileImage: {
    url: string;
  } | null;
  bio: string | null;
  country: string;
  joinedDate: string;
  isIdentityVerified: boolean;
}

interface BuyerStats {
  totalPosts: number;
  totalReviews: number;
  totalFollowers: number;
  totalFollowing: number;
}

interface BuyerRecentActivity {
  type: string;
  content: string;
  createdAt: string;
}

interface BuyerSelectedCategory {
  name: string;
  subCategories: {
    name: string;
  }[];
}

export interface BuyerPublicProfileResponse {
  success: boolean;
  message: string;
  data: {
    basicInfo: BuyerBasicInfo;
    stats: BuyerStats;
    recentActivity: BuyerRecentActivity[];
    selectedCategories: BuyerSelectedCategory[];
  };
}

export interface UpdateBuyerProfileRequest {
  firstName?: string;
  lastName?: string;
  username?: string;
  country?: string;
  phoneNumber?: string;
  bio?: string;
  notificationPreferences?: {
    email?: boolean;
    push?: boolean;
    sms?: boolean;
  };
  preferredLanguage?: string;
  preferredCurrency?: string;
}

export interface UpdateBuyerProfileResponse {
  success: boolean;
  message: string;
  data: {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    country: string;
    phoneNumber: string;
    bio: string;
    notificationPreferences: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    preferredLanguage: string;
    preferredCurrency: string;
    profilePictureUrl: string | null;
  };
}

export interface UploadAvatarResponse {
  success: boolean;
  message: string;
  data: {
    profileImage: any;
    profilePictureUrl: string;
    uploadedAt: string;
  };
}

export interface RemoveAvatarResponse {
  success: boolean;
  message: string;
  data: {
    profilePictureUrl: null;
    removedAt: string;
  };
}

export const transformFollowingStore = (
  item: FollowingItem
): FollowingStore => {
  return {
    id: item.id,
    name: item.storeName || item.name,
    avatar: item.profileImage,
    isLive: item.isLive,
    itemsCount: item.itemsCount,
  };
};

export const buyerService = {
  async getFollowingStores(
    params: GetFollowingStoresParams = {}
  ): Promise<{ stores: FollowingStore[]; pagination: PaginationData }> {
    const { page = 1, limit = 20, query } = params;
    const queryParam = query ? `&query=${encodeURIComponent(query)}` : '';
    const url = `/api/v2/buyers/following?page=${page}&limit=${limit}${queryParam}`;

    const response = await apiService.get<FollowingStoresResponse>(url);

    return {
      stores: response.data.following.map(transformFollowingStore),
      pagination: response.pagination,
    };
  },

  async getAllFollowingStores(): Promise<FollowingStore[]> {
    const allStores: FollowingStore[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const { stores, pagination } = await this.getFollowingStores({
        page: currentPage,
        limit: 50,
      });

      allStores.push(...stores);
      hasMore = pagination.hasMore;
      currentPage++;
    }

    return allStores;
  },

  async followStore(sellerId: string): Promise<FollowStoreResponse> {
    if (!sellerId) {
      throw new Error("Seller ID is required");
    }

    const url = `/api/v2/buyers/follow/${sellerId}`;
    return apiService.post<FollowStoreResponse>(url);
  },

  async unfollowStore(sellerId: string): Promise<UnfollowStoreResponse> {
    if (!sellerId) {
      throw new Error("Seller ID is required");
    }

    const url = `/api/v2/buyers/follow/${sellerId}`;
    return apiService.delete<UnfollowStoreResponse>(url);
  },

  async getBuyerPublicProfile(
    buyerId: string
  ): Promise<BuyerPublicProfileResponse> {
    if (!buyerId) {
      throw new Error("Buyer ID is required");
    }

    const url = `/api/v2/buyers/public-profile/${buyerId}`;
    return apiService.get<BuyerPublicProfileResponse>(url);
  },

  async updateBuyerProfile(
    profileData: UpdateBuyerProfileRequest
  ): Promise<UpdateBuyerProfileResponse> {
    const url = `/api/v2/buyers/profile`;
    return apiService.put<UpdateBuyerProfileResponse>(url, profileData as Record<string, unknown>);
  },

  /**
   * Upload buyer profile avatar
   *
   * @param file - Image file to upload (JPEG, PNG, GIF, WebP - max 5MB)
   * @returns Promise<UploadAvatarResponse> - Response with new avatar URL
   *
   * @example
   * ```typescript
   * const result = await buyerService.uploadAvatar(imageFile);
   * console.log(`Avatar uploaded: ${result.data.profilePictureUrl}`);
   * ```
   */
  async uploadAvatar(file: File): Promise<UploadAvatarResponse> {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB. Please upload a smaller image.');
    }

    const formData = new FormData();
    formData.append('avatar', file, file.name);

    const url = `/api/v2/buyers/profile/avatar`;
    
    // CRITICAL: Remove Content-Type header to let browser set multipart boundary
    // Pass FormData directly and override headers in config
    return apiService.put<UploadAvatarResponse>(url, formData as unknown as Record<string, unknown>, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Remove buyer profile avatar
   *
   * @returns Promise<RemoveAvatarResponse> - Response confirming removal
   *
   * @example
   * ```typescript
   * const result = await buyerService.removeAvatar();
   * console.log('Avatar removed successfully');
   * ```
   */
  async removeAvatar(): Promise<RemoveAvatarResponse> {
    const url = `/api/v2/buyers/profile/avatar`;
    return apiService.delete<RemoveAvatarResponse>(url);
  },
};